import { useState } from "react";
import { Ban, Plus, Ticket } from "lucide-react";
import { useCreateInvite, useInvites, useMe, useRevokeInvite } from "@/api/hooks";
import { errorMessage } from "@/api/client";
import type { CreatedInvite, Invite, InviteStatus } from "@/api/types";
import { formatDateTime, timeAgo } from "@/lib/utils";
import { useToast } from "@/lib/toast";
import { EmptyState, ErrorState, PageHeader } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CopyButton } from "@/components/ui/copy-button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageSkeleton } from "@/components/ui/skeleton";
import { SettingsNav } from "./SettingsConnect";

const STATUS_BADGE: Record<InviteStatus, "success" | "secondary" | "warning" | "destructive"> = {
  active: "success",
  used: "secondary",
  expired: "warning",
  revoked: "destructive",
};

export default function InvitesPage() {
  const me = useMe();
  const isAdmin = !!me.data?.isAdmin;
  const invites = useInvites(isAdmin);
  const create = useCreateInvite();
  const revoke = useRevokeInvite();
  const { toast } = useToast();
  const [note, setNote] = useState("");
  const [email, setEmail] = useState("");
  const [days, setDays] = useState("7");
  const [singleUse, setSingleUse] = useState(true);
  const [maxUses, setMaxUses] = useState("5");
  const [created, setCreated] = useState<CreatedInvite | null>(null);
  const [toRevoke, setToRevoke] = useState<Invite | null>(null);

  if (me.data && !isAdmin) {
    return (
      <div className="mx-auto max-w-3xl">
        <PageHeader title="Invites" />
        <SettingsNav />
        <p className="text-sm text-muted-foreground">Only admins can invite people.</p>
      </div>
    );
  }

  const daysNum = Number(days);
  const usesNum = singleUse ? 1 : Number(maxUses);
  const valid = Number.isInteger(daysNum) && daysNum >= 1 && daysNum <= 90 && Number.isInteger(usesNum) && usesNum >= 1 && usesNum <= 100;

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Invites" description="Links that let someone create a Minne account. No email is sent: copy the link and pass it on." />
      <SettingsNav />

      <form
        className="mb-8 grid gap-4 rounded-xl border bg-card p-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid) return;
          create.mutate(
            { note: note.trim() || undefined, email: email.trim() || undefined, expiresInDays: daysNum, maxUses: usesNum },
            {
              onSuccess: (r) => {
                setCreated(r);
                setNote("");
                setEmail("");
              },
              onError: (err) => toast({ title: "Could not create invite", description: errorMessage(err), tone: "error" }),
            },
          );
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <Label htmlFor="inv-note">Note</Label>
            <Input id="inv-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="for Anna" maxLength={200} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="inv-email">Lock to email (optional)</Label>
            <Input id="inv-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="anna@example.org" />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="inv-days">Expires after (days)</Label>
            <Input id="inv-days" type="number" min={1} max={90} value={days} onChange={(e) => setDays(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="inv-single">Uses</Label>
            <div className="flex h-9 items-center gap-3">
              <Switch id="inv-single" checked={singleUse} onCheckedChange={setSingleUse} />
              <span className="text-sm">{singleUse ? "Single use" : "Up to"}</span>
              {!singleUse && (
                <Input aria-label="Maximum uses" type="number" min={2} max={100} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} className="w-24" />
              )}
            </div>
          </div>
        </div>
        <div>
          <Button type="submit" disabled={!valid || create.isPending}>
            <Plus /> {create.isPending ? "Creating…" : "Create invite"}
          </Button>
        </div>
      </form>

      {invites.isLoading && <PageSkeleton />}
      {invites.isError && <ErrorState error={invites.error} retry={() => invites.refetch()} />}
      {invites.data && invites.data.length === 0 && <EmptyState icon={Ticket} title="No invites yet" description="Create one above and send the link to the person you want to invite." />}
      {invites.data && invites.data.length > 0 && (
        <ul className="divide-y rounded-xl border bg-card">
          {invites.data.map((inv) => (
            <li key={inv.id} className="flex items-start gap-3 px-4 py-3">
              <Ticket className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{inv.note || "Invite"}</span>
                  <Badge variant={STATUS_BADGE[inv.status]}>{inv.status}</Badge>
                  {inv.email && <Badge variant="outline">{inv.email}</Badge>}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  created {timeAgo(inv.createdAt)}
                  {inv.createdBy ? ` by ${inv.createdBy.displayName || inv.createdBy.username}` : ""} · {inv.uses}/{inv.maxUses} used · expires {formatDateTime(inv.expiresAt)}
                </div>
                {inv.usedBy.length > 0 && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    used by {inv.usedBy.map((u) => `${u.displayName || u.username} (${timeAgo(u.usedAt)})`).join(", ")}
                  </div>
                )}
              </div>
              {inv.status === "active" && (
                <Button variant="ghost" size="icon-sm" aria-label="Revoke invite" title="Revoke" onClick={() => setToRevoke(inv)}>
                  <Ban className="text-destructive" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!created} onOpenChange={(o) => !o && setCreated(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Invite created</DialogTitle>
            <DialogDescription>Copy the link now. It is shown only once; Minne keeps just a hash.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-lg border bg-muted/40 p-3">
            <code className="min-w-0 flex-1 break-all font-mono text-xs">{created?.url}</code>
            {created && <CopyButton text={created.url} label="Copy link" showLabel size="sm" variant="outline" />}
          </div>
          <DialogFooter>
            <Button onClick={() => setCreated(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toRevoke} onOpenChange={(o) => !o && setToRevoke(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke this invite?</DialogTitle>
            <DialogDescription>The link stops working. Accounts already created with it are not affected.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToRevoke(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={revoke.isPending}
              onClick={() =>
                toRevoke &&
                revoke.mutate(toRevoke.id, {
                  onSuccess: () => setToRevoke(null),
                  onError: (err) => toast({ title: "Could not revoke", description: errorMessage(err), tone: "error" }),
                })
              }
            >
              {revoke.isPending ? "Revoking…" : "Revoke"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
